// 018 — Pre Anestésico (Anexo 1 MSP, SNS-MSP/HCU-form. 018/2021). Bloques A-J del papel (páginas 81-82 del PDF); datos y
// decisiones en `bloques018Datos.ts`. Al guardar deja, en UNA transacción, una `Condition` por diagnóstico, la
// `AllergyIntolerance` de "Alérgicos" (si no es una negativa) y los `Observation` de signos vitales; el resto va como `Par[]`
// en el PDF firmado.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple } from './camposSimplesDatos';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  alergiaDe018,
  CAMPOS_ANTECEDENTES_018,
  CAMPOS_ANTROPOMETRIA_018,
  CAMPOS_CONSTANTES_018,
  CAMPOS_ESCALAS_018,
  CAMPOS_GABINETE_018,
  CAMPOS_INDICACIONES_018,
  CAMPOS_INGESTA_018,
  CAMPOS_PLAN_018,
  CAMPOS_REGIONES_018,
  CAMPOS_VALORACION_018,
  CAMPOS_VIA_AEREA_018,
  COLUMNAS_PATOLOGICOS_018,
  conditionsDe018,
  estadoInicial018,
  FILAS_PATOLOGICOS_018,
  GRUPOS_LABORATORIO_018,
  observacionesDe018,
  paresDe018,
  type Estado018,
} from './bloques018Datos';

const CODIGO = '018';

type ClaveValores = 'valoracion' | 'antecedentes' | 'constantes' | 'antropometria' | 'viaAerea' | 'regiones' | 'laboratorio' | 'gabinete' | 'escalas' | 'ingesta' | 'indicaciones' | 'plan';

export function Formulario018({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f018.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Valoración preanestésica: el papel no trae servicio y puede ser ambulatoria o de hospitalizado; no se afirma 'Consulta externa'.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { sinServicio: true });

  const [estado, setEstado] = useState<Estado018>(() => estadoInicial018());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahoraIso = new Date().toISOString();
      const lote = new LoteTransaccional();
      for (const c of conditionsDe018(estado, subject, encounter, ahoraIso)) lote.crear(c);
      const alergia = alergiaDe018(estado, subject, ahoraIso);
      if (alergia) lote.crear(alergia);
      for (const o of observacionesDe018(estado, subject, encounter, ahoraIso)) lote.crear(o);
      await lote.enviar(medplum);
      onGuardado(paresDe018(t, estado), datosEncabezadoMsp);
    });

  function bloque(clave: ClaveValores, tituloClave: string, prefijo: string, lista: readonly CampoSimple[], columnas?: 2 | 3 | 4): JSX.Element {
    return (
      <Bloque titulo={t(`formularios.f018.${tituloClave}`)}>
        <CamposSimples prefijoId={`f018-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} onChange={(v) => setEstado((prev) => ({ ...prev, [clave]: v }))} />
      </Bloque>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f018.valoracionTitulo')}>
        <div className="flex flex-col gap-2">
          {estado.diagnosticos.map((d, i) => (
            <SelectorDiagnosticoCie10
              key={i}
              label={`${t('formularios.f018.diagnosticoPregunta')} ${i + 1}`}
              value={d}
              onChange={(v) => setEstado((prev) => ({ ...prev, diagnosticos: prev.diagnosticos.map((x, j) => (j === i ? v : x)) }))}
            />
          ))}
        </div>
        <CamposSimples prefijoId="f018-val" campos={CAMPOS_VALORACION_018} valores={estado.valoracion} columnas={3} onChange={(v) => setEstado((prev) => ({ ...prev, valoracion: v }))} />
      </Bloque>

      <Bloque titulo={t('formularios.f018.patologicosTitulo')}>
        <TablaConColumnas filas={FILAS_PATOLOGICOS_018} columnas={COLUMNAS_PATOLOGICOS_018} estado={estado.patologicos} onChange={(p) => setEstado((prev) => ({ ...prev, patologicos: p }))} />
      </Bloque>
      {bloque('antecedentes', 'antecedentesTitulo', 'ant', CAMPOS_ANTECEDENTES_018, 2)}

      {bloque('constantes', 'constantesTitulo', 'cv', CAMPOS_CONSTANTES_018, 3)}
      {bloque('antropometria', 'antropometriaTitulo', 'antrop', CAMPOS_ANTROPOMETRIA_018, 3)}
      {bloque('viaAerea', 'viaAereaTitulo', 'via', CAMPOS_VIA_AEREA_018, 3)}
      {bloque('regiones', 'regionesTitulo', 'reg', CAMPOS_REGIONES_018, 2)}

      <Bloque titulo={t('formularios.f018.laboratorioTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f018.laboratorioNota')}</p>
        {GRUPOS_LABORATORIO_018.map((g) => (
          <div key={g.clave} className="flex flex-col gap-2">
            <h5 className="text-xs font-semibold uppercase tracking-wide text-hce-muted">{g.titulo}</h5>
            <CamposSimples prefijoId={`f018-lab-${g.clave}`} campos={g.campos} valores={estado.laboratorio} columnas={4} onChange={(v) => setEstado((prev) => ({ ...prev, laboratorio: v }))} />
          </div>
        ))}
      </Bloque>
      {bloque('gabinete', 'gabineteTitulo', 'gab', CAMPOS_GABINETE_018, 2)}

      {bloque('escalas', 'escalasTitulo', 'esc', CAMPOS_ESCALAS_018, 3)}
      {bloque('ingesta', 'ingestaTitulo', 'ing', CAMPOS_INGESTA_018, 4)}
      {bloque('indicaciones', 'indicacionesTitulo', 'ind', CAMPOS_INDICACIONES_018, 2)}
      {bloque('plan', 'planTitulo', 'plan', CAMPOS_PLAN_018, 2)}

      <Bloque titulo={t('formularios.f018.responsableTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
